import { useEffect, useRef, useCallback } from "react";
import echo from "../lib/echo";

/**
 * Custom hook to manage real-time question updates via WebSocket
 *
 * @param {Array} questions - Array of questions
 * @param {Function} setQuestions - State setter for questions
 * @param {string} projectId - Current project ID
 */
export function useQuestionRealtime(questions, setQuestions, projectId) {
  const pendingActionsRef = useRef(new Set());
  const subscribedChannelsRef = useRef(new Set());

  // Track actions initiated by this user
  const addPendingAction = useCallback((type, id) => {
    const key = `${type}-${id}`;
    pendingActionsRef.current.add(key);

    // Note: Removed auto-timeout to ensure pending actions persist until WebSocket events are processed
    // The WebSocket handler will remove the pending action when it skips the event
  }, []);

  const isPendingAction = useCallback((type, id) => {
    const key = `${type}-${id}`;
    return pendingActionsRef.current.has(key);
  }, []);

  // Subscribe to WebSocket events for the project
  useEffect(() => {
    if (!projectId) return;

    const channelName = `project.${projectId}`;

    // Skip if already subscribed
    if (subscribedChannelsRef.current.has(channelName)) {
      return;
    }

    const channel = echo
      .channel(channelName)
      .listen(".question.added", (event) => {
        // Validate event structure
        if (!event || !event.question) {
          return;
        }

        const questionId = event.question.id;

        // Skip if this user just added it
        if (isPendingAction("add", questionId)) {
          pendingActionsRef.current.delete(`add-${questionId}`);
          return;
        }

        // Add question with animation flag for new questions from other users
        setQuestions((prev) => {
          // Validate incoming data
          if (!event.question || event.question.id === undefined || event.question.id === null) {
            return prev;
          }

          // Prevent duplicates (normalize ids to numbers)
          const incomingId = Number(event.question.id);
          
          // Check if incomingId is a valid number (not NaN)
          if (isNaN(incomingId)) {
            return prev;
          }

          if (prev.some((q) => Number(q.id) === incomingId)) {
            return prev;
          }

          // Add animation flag for new questions from other users
          const questionWithAnimation = {
            ...event.question,
            comments: [], // Initialize empty comments array
            status: event.question?.status || "untouched",
            _isNew: true,
          };

          return [...prev, questionWithAnimation];
        });

        // Remove animation flag after animation completes
        setTimeout(() => {
          setQuestions((prev) =>
            prev.map((q) =>
              Number(q.id) === Number(event.question.id)
                ? { ...q, _isNew: false, status: q?.status || "untouched" }
                : q,
            ),
          );
        }, 300);

        // Forward notification payload to notifications provider
        if (event.notification) {
          try {
            window.dispatchEvent(
              new CustomEvent("app:new-notification", { detail: event.notification }),
            );
          } catch (e) {}
        }
      })
      .listen(".question.deleted", (event) => {
        // Skip if this user just deleted it
        if (isPendingAction("delete", event.question_id)) {
          pendingActionsRef.current.delete(`delete-${event.question_id}`);
          return;
        }

        // Mark question for deletion with animation flag
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === event.question_id ? { ...q, _isDeleting: true } : q,
          ),
        );

        // Remove question after animation completes
        setTimeout(() => {
          setQuestions((prev) =>
            prev.filter((q) => q.id !== event.question_id),
          );
        }, 200);
      });

    subscribedChannelsRef.current.add(channelName);

    // Cleanup: unsubscribe from channel
    return () => {
      echo.leave(channelName);
      subscribedChannelsRef.current.delete(channelName);
    };
  }, [projectId, isPendingAction]);

  // Return methods to track user actions
  return {
    addPendingAction,
    isPendingAction,
  };
}

export default useQuestionRealtime;
